'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import {
  applyDynamicSvgStyles,
  parseSvgShapeSettingsJson,
  type SvgShapeSettings,
} from './applyDynamicSvgStyles';
import { svgMarkupToDataUri } from './viewingChromeHelpers';

export interface DynamicSvgShapeHostProps {
  src: string;
  svgShapeSettings?: string | null;
  backgroundColor: string;
  borderColor: string;
  frameThickness?: number;
  textColor?: string;
  width: number;
  height: number;
  style?: CSSProperties;
}

/**
 * Containd unlocked shapes stretch (`preserveAspectRatio="none"` → CSS fill).
 * Locked shapes letterbox (`xMidYMid meet` → CSS contain).
 */
export function shapeSvgObjectFit(
  isAspectLocked: boolean | undefined,
): 'fill' | 'contain' {
  return isAspectLocked === true ? 'contain' : 'fill';
}

export function shapeSvgPreserveAspectRatio(
  isAspectLocked: boolean | undefined,
): 'none' | 'xMidYMid meet' {
  return isAspectLocked === true ? 'xMidYMid meet' : 'none';
}

/**
 * HTML twin of Containd DynamicSvgShapeImage for generic item thumbs.
 * Fetches SVG XML and applies auto-id color/stroke patches, then paints as
 * `<img>` (SVG-as-image — scripts/handlers do not run). Prefer host-baked
 * `shaped/…` bytes when present (VISION: Dynamic SVG shape is host-resolved
 * media). This fetch+recolor path remains for unshaped packages only.
 * Always keeps an <img> fallback until styled XML is ready (SSR-safe).
 */
export function DynamicSvgShapeHost({
  src,
  svgShapeSettings,
  backgroundColor,
  borderColor,
  frameThickness,
  textColor,
  width,
  height,
  style,
}: DynamicSvgShapeHostProps) {
  const [xml, setXml] = useState<string | null>(null);
  const settings = parseSvgShapeSettingsJson(svgShapeSettings);
  const objectFit = shapeSvgObjectFit(settings?.isAspectLocked);
  const preserveAspectRatio = shapeSvgPreserveAspectRatio(
    settings?.isAspectLocked,
  );

  useEffect(() => {
    let active = true;
    if (!src || !settings?.isDynamicSVG) {
      setXml(null);
      return;
    }
    void (async () => {
      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        if (!active) return;
        if (!text.includes('<')) {
          setXml(null);
          return;
        }
        setXml(
          applyDynamicSvgStyles(text, settings as SvgShapeSettings, {
            backgroundColor,
            borderColor,
            frameThickness,
            textColor,
            itemWidth: width,
          }),
        );
      } catch {
        if (active) setXml(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [
    src,
    svgShapeSettings,
    backgroundColor,
    borderColor,
    frameThickness,
    textColor,
    width,
    settings,
  ]);

  const boxStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit,
    ...style,
  };

  if (xml) {
    const sized = xml.includes('width=')
      ? xml
      : xml.replace(
          /<svg\b/i,
          `<svg width="100%" height="100%" preserveAspectRatio="${preserveAspectRatio}"`,
        );
    // eslint-disable-next-line @next/next/no-img-element
    return (
      <img
        src={svgMarkupToDataUri(sized)}
        alt=""
        data-svg-shape="dynamic"
        data-svg-aspect={objectFit}
        style={boxStyle}
      />
    );
  }

  // SSR / pending / fetch fail: keep raster/SVG <img> so items never go blank.
  // Host-baked shaped/… bytes already include preserveAspectRatio; CSS objectFit
  // must still stretch/letterbox to match Containd SvgXml box fill.
  // eslint-disable-next-line @next/next/no-img-element
  return (
    <img
      src={src}
      alt=""
      data-svg-shape={settings?.isDynamicSVG ? 'pending' : 'static'}
      data-svg-aspect={objectFit}
      style={boxStyle}
    />
  );
}
